/* ==========================================================================
   AURVELLE — Premium Motion System  (Phase 2)
   --------------------------------------------------------------------------
   <aurvelle-reveal> — a presentational scroll-reveal wrapper.

   The element only ever toggles its own opacity / transform / clip-path
   (and, for staggered mode, a `--aw-reveal-index` custom property on its
   direct children). The actual animation lives in assets/aurvelle.css.
   It never listens to Shopify events, never touches forms, buy buttons,
   the cart drawer, or product interactions — wrap editorial content only.

   Design contract
   - Desktop (>=990px): IntersectionObserver root is `.page-wrapper`
     (Horizon's desktop scroll container). Mobile (<990px): viewport.
   - Fully honours prefers-reduced-motion — under `reduce` the component
     is completely inert and content renders in its natural, visible state.
   - Morph-safe: the "hidden" state is applied by JS (attribute
     `data-reveal-pending`), never present in server markup, so a failed
     script or a morph re-render can never leave content invisible.
   - View Transitions safe: no `view-transition-name`, no `::view-transition`
     usage, no navigation hooks.

   Authoring API
     <aurvelle-reveal>...</aurvelle-reveal>            default: fade + rise
     reveal="fade|rise|scale|heading|image|card"
     stagger="80"     ms between direct children (enables per-child reveal)
     delay="0"        ms before the reveal starts
     distance="24"    px of travel for rise/card
     duration="900"   ms transition duration
     immediate        arm even if already on screen (hero entrance)
     parallax="0.08"  subtle vertical parallax instead of a reveal
     once             (default) reveal a single time and stop observing
   ========================================================================== */

const REDUCE_QUERY = '(prefers-reduced-motion: reduce)';
const DESKTOP_QUERY = '(min-width: 990px)';
const ROOT_MARGIN = '0px 0px -12% 0px';
const SAFETY_MS = 6000;
const PARALLAX_MAX_PX = 48;

const reduceMotion = window.matchMedia(REDUCE_QUERY);
const desktop = window.matchMedia(DESKTOP_QUERY);

/** Shared IntersectionObservers, keyed by scroll root ('viewport' or the node). */
const revealObservers = new Map();
const parallaxObservers = new Map();

/** Elements still waiting to be revealed — used on reduce-motion changes / resize. */
const pending = new Set();

function currentScrollRoot() {
  if (desktop.matches) return document.querySelector('.page-wrapper') || null;
  return null;
}

function rootKey(root) {
  return root || 'viewport';
}

function getRevealObserver(root) {
  const key = rootKey(root);
  let observer = revealObservers.get(key);
  if (!observer) {
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const el = entry.target;
          if (!(el instanceof AurvelleReveal)) continue;
          if (entry.isIntersecting) {
            el.reveal();
          } else if (
            entry.rootBounds &&
            entry.boundingClientRect.bottom <= entry.rootBounds.top
          ) {
            // Scrolled past with no intersecting frame (fast / jump scroll):
            // the element is entirely above the viewport now — just show it,
            // no animation, so it is never left hidden.
            el.reveal();
          }
        }
      },
      { root, rootMargin: ROOT_MARGIN, threshold: 0 }
    );
    revealObservers.set(key, observer);
  }
  return observer;
}

function getParallaxObserver(root) {
  const key = rootKey(root);
  let observer = parallaxObservers.get(key);
  if (!observer) {
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target instanceof AurvelleReveal) {
            entry.target.setParallaxActive(entry.isIntersecting);
          }
        }
      },
      { root, rootMargin: '40% 0px 40% 0px', threshold: 0 }
    );
    parallaxObservers.set(key, observer);
  }
  return observer;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/* --------------------------------------------------------------------------
   Shared parallax loop — one rAF for every currently-active parallax element.
   Runs only while at least one parallax element is near the viewport, so
   there is no "constant motion" when nothing is on screen.
   -------------------------------------------------------------------------- */
const parallaxActive = new Set();
let parallaxFrame = 0;

function parallaxTick() {
  const viewportH = window.innerHeight || document.documentElement.clientHeight;
  for (const el of parallaxActive) {
    const rect = el.getBoundingClientRect();
    const progress = (rect.top + rect.height / 2 - viewportH / 2) / viewportH;
    const offset = clamp(-progress * el.parallaxStrength * viewportH, -PARALLAX_MAX_PX, PARALLAX_MAX_PX);
    el.style.setProperty('--aw-parallax-y', `${offset.toFixed(1)}px`);
  }
  parallaxFrame = parallaxActive.size > 0 ? requestAnimationFrame(parallaxTick) : 0;
}

function ensureParallaxLoop() {
  if (!parallaxFrame && parallaxActive.size > 0) {
    parallaxFrame = requestAnimationFrame(parallaxTick);
  }
}

export class AurvelleReveal extends HTMLElement {
  /** @type {boolean} */
  #armed = false;
  /** @type {boolean} */
  #revealed = false;
  /** @type {number} */
  #safetyTimer = 0;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {IntersectionObserver | null} */
  #parallaxObserver = null;
  /** @type {number} */
  parallaxStrength = 0;

  connectedCallback() {
    // Defer one frame so a getBoundingClientRect() read never forces layout
    // mid-parse, and so morph re-inserts settle before we measure.
    requestAnimationFrame(() => this.#init());
  }

  disconnectedCallback() {
    this.#teardown();
  }

  #init() {
    if (!this.isConnected || this.#revealed) return;

    // Reduced motion: stay completely out of the way.
    if (reduceMotion.matches) {
      this.#markRevealed(false);
      return;
    }

    const parallaxAttr = this.getAttribute('parallax');
    if (parallaxAttr !== null && !this.hasAttribute('reveal')) {
      this.parallaxStrength = clamp(parseFloat(parallaxAttr) || 0.08, -0.2, 0.2);
      this.#parallaxObserver = getParallaxObserver(currentScrollRoot());
      this.#parallaxObserver.observe(this);
      return;
    }

    // Pass author options to CSS.
    const stagger = this.getAttribute('stagger');
    if (stagger !== null) {
      this.style.setProperty('--aw-stagger', `${parseFloat(stagger) || 80}ms`);
      const children = Array.from(this.children);
      children.forEach((child, index) => {
        if (child instanceof HTMLElement) child.style.setProperty('--aw-reveal-index', String(index));
      });
    }
    const delay = this.getAttribute('delay');
    if (delay !== null) this.style.setProperty('--aw-reveal-delay', `${parseFloat(delay) || 0}ms`);
    const duration = this.getAttribute('duration');
    if (duration !== null) this.style.setProperty('--aw-reveal-duration', `${parseFloat(duration) || 900}ms`);
    const distance = this.getAttribute('distance');
    if (distance !== null) this.style.setProperty('--aw-reveal-distance', `${parseFloat(distance) || 24}px`);

    // Fast path: already on screen (initial above-the-fold content, or a morph
    // re-insert after the user has scrolled) — show it now, no animation, no flash.
    if (!this.hasAttribute('immediate') && this.#isWithinRevealLine()) {
      this.#markRevealed(false);
      return;
    }

    this.setAttribute('data-reveal-pending', '');
    this.#armed = true;
    pending.add(this);

    if (this.hasAttribute('immediate')) {
      // Hero entrance: reveal on the next frame so the transition can run.
      requestAnimationFrame(() => requestAnimationFrame(() => this.reveal()));
      return;
    }

    this.#observer = getRevealObserver(currentScrollRoot());
    this.#observer.observe(this);

    // Belt-and-braces: if the element is on screen but the observer somehow
    // never fired (element revealed by a display toggle, IO quirk, etc.),
    // reveal it after a grace period. Off-screen elements are left armed —
    // the IntersectionObserver stays responsible for those on scroll, so
    // normal below-the-fold content is never pre-revealed.
    this.#safetyTimer = window.setTimeout(() => {
      if (this.#isOnScreen()) this.reveal();
    }, SAFETY_MS);
  }

  /** @returns {number} the scroll root's viewport-bottom in px. */
  #viewportBottom() {
    const root = currentScrollRoot();
    return root ? root.getBoundingClientRect().bottom : window.innerHeight;
  }

  /**
   * Fast-path test: is the element already at or above the reveal line
   * (in view, or scrolled past)? Such elements show immediately, no animation.
   */
  #isWithinRevealLine() {
    return this.getBoundingClientRect().top < this.#viewportBottom() * 0.88;
  }

  /** Is the element actually within the viewport right now? (safety-timer only) */
  #isOnScreen() {
    const rect = this.getBoundingClientRect();
    return rect.top < this.#viewportBottom() && rect.bottom > 0;
  }

  reveal() {
    if (this.#revealed) return;
    this.#markRevealed(true);
  }

  #markRevealed(animate) {
    this.#revealed = true;
    this.#armed = false;
    pending.delete(this);
    window.clearTimeout(this.#safetyTimer);
    if (this.#observer) {
      this.#observer.unobserve(this);
      this.#observer = null;
    }
    this.removeAttribute('data-reveal-pending');
    this.classList.add('is-revealed');

    if (!animate) return;
    // Drop will-change once the transition has had time to finish.
    const total =
      (parseFloat(this.getAttribute('duration')) || 900) + (parseFloat(this.getAttribute('delay')) || 0) + 120;
    window.setTimeout(() => {
      this.style.removeProperty('will-change');
    }, total);
  }

  /** @param {boolean} active */
  setParallaxActive(active) {
    if (active) {
      parallaxActive.add(this);
      ensureParallaxLoop();
    } else {
      parallaxActive.delete(this);
      this.style.setProperty('--aw-parallax-y', '0px');
    }
  }

  #teardown() {
    window.clearTimeout(this.#safetyTimer);
    pending.delete(this);
    parallaxActive.delete(this);
    if (this.#observer) this.#observer.unobserve(this);
    if (this.#parallaxObserver) this.#parallaxObserver.unobserve(this);
    this.#observer = null;
    this.#parallaxObserver = null;
  }
}

/* If the viewport crosses the 990px boundary the scroll root changes; the
   observer root can't be mutated, so re-arm anything still pending against
   a fresh observer for the new root. Debounced, and a no-op when idle. */
let resizeTimer = 0;
let wasDesktop = desktop.matches;
window.addEventListener(
  'resize',
  () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      if (desktop.matches === wasDesktop) return;
      wasDesktop = desktop.matches;
      const root = currentScrollRoot();
      for (const el of pending) {
        el.reArm(root);
      }
    }, 200);
  },
  { passive: true }
);

// Small helper on the prototype for the resize handler above.
AurvelleReveal.prototype.reArm = function reArm(root) {
  if (this._reArmObserver) this._reArmObserver.unobserve(this);
  const observer = getRevealObserver(root);
  observer.observe(this);
  this._reArmObserver = observer;
};

/* If the user switches on reduced motion mid-session, reveal everything now. */
const onReduceChange = () => {
  if (!reduceMotion.matches) return;
  for (const el of Array.from(pending)) el.reveal();
};
if (typeof reduceMotion.addEventListener === 'function') {
  reduceMotion.addEventListener('change', onReduceChange);
}

if (!customElements.get('aurvelle-reveal')) {
  customElements.define('aurvelle-reveal', AurvelleReveal);
}
